/**
 * Navegação do painel — FONTE ÚNICA (03/09).
 *
 * A barra lateral (`AdminShell`) e as rotas (`AppRoutes`) leem daqui. Enquanto
 * eram duas listas escritas à mão, o modo de falha era mudo: um destino podia
 * existir na barra e não nas rotas (link para tela branca) ou o contrário (tela
 * inalcançável). É a mesma classe de defeito que já custou caro neste projeto
 * quando duas superfícies mantinham catálogos paralelos do mesmo dado.
 *
 * O agrupamento é por RITMO, não por assunto: diário na frente, episódico
 * atrás de um destino só. Ver o docblock do `AdminShell` para o porquê.
 *
 * ⚠️ Destino novo entra aqui e nos DOIS consumidores no mesmo commit —
 * `adminNavigation.test.ts` cobra que barra e rotas descrevam o mesmo conjunto.
 */

export interface AdminSubTab {
  /** Segmento relativo ao destino (`denuncias` → `/admin/entrada/denuncias`). */
  to: string;
  label: string;
}

export interface AdminDestinationDef {
  /** Segmento relativo a `/admin`. */
  to: string;
  label: string;
  /** Sub-aba aberta pelo `index` do destino. */
  defaultTab: string;
  tabs: readonly AdminSubTab[];
}

export const ADMIN_DESTINATIONS = [
  {
    // A home do painel. Só existe porque o agregado diário existe: antes da
    // Onda 1 o `/admin` abria a Entrada, porque destino vazio é pior que
    // destino nenhum.
    to: "pulso",
    label: "Pulso",
    defaultTab: "hoje",
    tabs: [
      { to: "hoje", label: "Hoje" },
      { to: "economia", label: "Economia" },
    ],
  },
  {
    to: "entrada",
    label: "Entrada",
    // A lista unificada é a porta: as três filas somadas respondem "o que
    // precisa de mim agora?", e as abas por fonte viram recorte de dentro dela.
    defaultTab: "tudo",
    tabs: [
      { to: "tudo", label: "Tudo" },
      { to: "conversas", label: "Conversas" },
      { to: "denuncias", label: "Denúncias" },
      { to: "catalogo", label: "Catálogo" },
      { to: "feedback", label: "Feedback" },
    ],
  },
  {
    to: "pessoas",
    label: "Pessoas",
    defaultTab: "contas",
    tabs: [
      { to: "contas", label: "Contas" },
      { to: "pro", label: "Acesso PRO" },
      { to: "creators", label: "Creators" },
      // Pré-cadastro "Sou autor" (A1a): lista de espera, só leitura. A A1c
      // vira fila de aprovação na MESMA aba.
      { to: "autores", label: "Autores" },
    ],
  },
  {
    to: "catalogo",
    label: "Catálogo",
    defaultTab: "curadoria",
    tabs: [
      { to: "curadoria", label: "Curadoria" },
      { to: "comunidade", label: "Comunidade" },
      // Livra Autores A5: anunciar lançamento (o caminho do admin roda hoje).
      { to: "lancamentos", label: "Lançamentos" },
      { to: "saude", label: "Saúde" },
    ],
  },
  {
    to: "alavancas",
    label: "Alavancas",
    // "Em destaque" foi pedido textualmente pelo fundador, e Temas é o que
    // ganha o lugar: medido, Campanhas ainda não tem consumidor no app (a
    // coleção promo_campaigns segue não fiada), então abrir nela é abrir
    // numa tela que não move nada.
    defaultTab: "temas",
    tabs: [
      // Temas é EPISÓDICO (cria-se um tema de vez em quando, não todo dia),
      // e a doutrina deste arquivo agrupa por ritmo. Por isso ele mora aqui e
      // não como destino próprio — promover é uma linha, no dia em que a Onda
      // 2 (criar tema com cores próprias) o fizer crescer.
      { to: "temas", label: "Temas" },
      // Loja (Bloco A, Admin-Alavancas, 14/09): store_items — flair, moldura,
      // cor do nome, banner, cena de Foco. Tema fica em "Temas" (editor
      // próprio desde a Onda 1/2).
      { to: "loja", label: "Loja" },
      // Afiliados é episódico pelo mesmo critério de ritmo: a tag muda quase
      // nunca. Mas o interruptor por plataforma é a única forma de tirar uma
      // rede do ar sem revisão de loja, então ele precisa estar a poucos
      // toques quando for preciso.
      { to: "afiliados", label: "Afiliados" },
      { to: "campanhas", label: "Campanhas" },
      { to: "avisos", label: "Avisos" },
      { to: "beta", label: "Beta" },
      // Interruptores (Bloco C, Admin-Alavancas, 14/09): app_config/runtime —
      // flags, produtos IAP e versão mínima, sem build. Mesmo critério de
      // ritmo dos vizinhos: mexe-se pouco, mas precisa estar a poucos toques
      // quando algo precisa ser desligado na hora (ex.: crédito de IA acabou).
      { to: "interruptores", label: "Interruptores" },
      // Conquistas (Bloco G, Admin-Alavancas, 15/09): badge_meta/quest_meta —
      // rótulo/emoji/descrição/raridade/visibilidade de badges e
      // rótulo/exibição de moeda/kill switch de missões diárias, sem build.
      // Mesmo critério de ritmo dos vizinhos: mexe-se pouco.
      { to: "conquistas", label: "Conquistas" },
    ],
  },
  {
    // Bloco B, Admin-Alavancas (14/09): antes deste destino o admin era CEGO
    // para ligas — nem uma métrica no Pulso, moderação só pelo console. Não
    // entra em Alavancas: ao contrário de Temas/Loja/Interruptores (mexe-se
    // uma vez a cada tanto), a moderação de sala e o "como foi a semana?" é
    // consulta e ação de RITMO próprio, do tamanho de Pessoas ou Catálogo.
    to: "ligas",
    label: "Ligas",
    defaultTab: "semana",
    tabs: [
      { to: "semana", label: "Semana" },
      { to: "salas", label: "Salas" },
      { to: "temporada", label: "Temporada" },
      // 24/09 (BACKLOG §0 item 10): a temporada AO VIVO — escada, estrelas,
      // metas. Só leitura; "Temporada" acima segue sendo o calendário.
      { to: "escada", label: "Escada" },
      // 24/09 (BACKLOG §0 item 10): de onde veio o XP de cada pessoa — a
      // auditoria de fraude do fechamento da temporada.
      { to: "extrato", label: "Extrato" },
    ],
  },
] as const satisfies readonly AdminDestinationDef[];

/**
 * O destino que `/admin` abre.
 *
 * Passou a ser o Pulso quando o agregado diário nasceu (Onda 1). Antes disso
 * era a Entrada: uma home que não tem o que mostrar manda o operador para uma
 * tela vazia todo dia, e o trabalho do dia está nas filas.
 */
export const ADMIN_DEFAULT_DESTINATION = "pulso";

export function adminTabsFor(destination: string): readonly AdminSubTab[] {
  return ADMIN_DESTINATIONS.find((d) => d.to === destination)?.tabs ?? [];
}
